import { escapeHtml } from '../lib/escape';
import {
  isChoice,
  isPhoto,
  notifyContent,
  notifyStructure,
  state,
  subscribeStructure,
} from '../state';
import type { AnswerPrompt, ChoiceQuestion, PhotoQuestion, Question } from '../types';
import { renderPhotoBlock } from './photo-block';
import { fetchRandomPortraitFile, openPortraitPicker } from './portrait-picker';
import { renderPromptsSection } from './prompt-list';
import { openWebcamModal } from './webcam-modal';

interface Elements {
  container: HTMLElement;
  panel: HTMLElement;
  editModeBtn: HTMLButtonElement;
  randomizeBtn: HTMLButtonElement;
}

let promptSeq = 0;
function freshPromptId(): string {
  return `ap-${Date.now().toString(36)}-${(promptSeq++).toString(36)}`;
}

export function mountQuestionsPanel(elements: Elements): void {
  const { container, panel, editModeBtn, randomizeBtn } = elements;

  const render = (): void => {
    panel.classList.toggle('edit-mode', state.questionsEditMode);
    panel.classList.toggle('compact-mode', !state.questionsEditMode);
    editModeBtn.classList.toggle('active', state.questionsEditMode);
    container.innerHTML = state.questions
      .map((q, i) =>
        isChoice(q)
          ? state.questionsEditMode
            ? renderChoiceBlockEdit(q, i)
            : renderChoiceBlockCompact(q, i)
          : renderPhotoBlock(q, i, state.questionsEditMode, state.lockedQuestionIds.has(q.id)),
      )
      .join('');
  };

  container.addEventListener('click', onClick);
  container.addEventListener('input', onInput);
  container.addEventListener('change', onChange);
  container.addEventListener('dragstart', onDragStart);
  container.addEventListener('dragenter', onDragEnter);
  container.addEventListener('dragover', onDragOver);
  container.addEventListener('dragleave', onDragLeave);
  container.addEventListener('drop', onDrop);
  container.addEventListener('dragend', onDragEnd);

  editModeBtn.addEventListener('click', () => {
    state.questionsEditMode = !state.questionsEditMode;
    notifyStructure();
  });

  randomizeBtn.addEventListener('click', () => {
    void randomizeAll();
  });

  subscribeStructure(render);
  render();
}

async function randomizeAll(): Promise<void> {
  state.questions.forEach((q, i) => {
    if (state.lockedQuestionIds.has(q.id)) return;
    if (isChoice(q) && q.options.length > 0) {
      // TODO(targets-multi-select): pick up to maxSelections once multi-select conflict resolution ships.
      const idx = Math.floor(Math.random() * q.options.length);
      q.selected = [idx];
      updateChoiceSelectionDom(i, q.selected);
    }
  });
  notifyContent();

  const photoQuestions = state.questions.filter(
    (q) => isPhoto(q) && !state.lockedQuestionIds.has(q.id),
  );
  if (photoQuestions.length === 0) return;
  const files = await Promise.all(photoQuestions.map(() => fetchRandomPortraitFile()));
  let changed = false;
  state.questions.forEach((q, i) => {
    if (!isPhoto(q)) return;
    if (state.lockedQuestionIds.has(q.id)) return;
    const file = files[photoQuestions.indexOf(q)];
    if (file) {
      q.file = file;
      updatePhotoBlockDom(i, q);
      changed = true;
    }
  });
  if (changed) notifyContent();
}

function updatePhotoBlockDom(qIndex: number, q: PhotoQuestion): void {
  const block = document.querySelector<HTMLElement>(
    `.question-block[data-q-index="${qIndex}"][data-kind="photo"]`,
  );
  if (!block) return;
  const tpl = document.createElement('template');
  tpl.innerHTML = renderPhotoBlock(q, qIndex, state.questionsEditMode, state.lockedQuestionIds.has(q.id)).trim();
  const next = tpl.content.firstElementChild;
  if (next) block.replaceWith(next);
}

function updateChoiceSelectionDom(qIndex: number, selected: number[]): void {
  const block = document.querySelector<HTMLElement>(
    `.question-block[data-q-index="${qIndex}"][data-kind="choice"]`,
  );
  if (!block) return;
  const set = new Set(selected);
  block.querySelectorAll<HTMLElement>('.option-chip').forEach((el, oi) => {
    el.classList.toggle('selected', set.has(oi));
  });
  block.querySelectorAll<HTMLElement>('.option-row').forEach((el, oi) => {
    el.classList.toggle('selected-row', set.has(oi));
  });
  block.querySelectorAll<HTMLElement>('.option-radio').forEach((el, oi) => {
    el.classList.toggle('selected', set.has(oi));
  });
}

function renderChoiceBlockEdit(q: ChoiceQuestion, index: number): string {
  const selectedSet = new Set(q.selected);
  const optionsHtml = q.options
    .map((opt, oi) => {
      const isSelected = selectedSet.has(oi);
      const removeBtn =
        q.options.length > 2
          ? `<button type="button" class="remove-option-btn" data-action="remove" data-o="${oi}" title="Remove">×</button>`
          : '';
      return `
        <div class="option-row${isSelected ? ' selected-row' : ''}" data-o="${oi}">
          <div class="option-label-side">
            <div class="option-radio${isSelected ? ' selected' : ''}" data-action="select" data-o="${oi}"></div>
            <input
              class="option-label-input"
              type="text"
              value="${escapeHtml(opt.label)}"
              data-action="label"
              data-o="${oi}"
            />
            ${removeBtn}
          </div>
          ${renderPromptsSection(opt.prompts, oi)}
        </div>
      `;
    })
    .join('');

  return `
    <div class="question-block" data-q-index="${index}" data-color="${q.colorIndex}" data-kind="choice">
      <div class="q-header" data-action="toggle">
        <span class="q-number">Q${index}</span>
        <input
          class="q-title-input"
          type="text"
          value="${escapeHtml(q.title)}"
          data-action="title"
          data-stop-toggle
        />
        <span class="q-toggle"></span>
      </div>
      <div class="question-body">
        ${renderHeadingHintInputs(q.heading, q.hint)}
        ${optionsHtml}
        <button type="button" class="add-option-btn" data-action="add">+ Add option</button>
      </div>
    </div>
  `;
}

function promptsContainerFor(q: Question, optionIndex: number): AnswerPrompt[] | null {
  if (isPhoto(q)) return q.prompts;
  if (isChoice(q)) {
    const opt = q.options[optionIndex];
    return opt ? opt.prompts : null;
  }
  return null;
}

function renderHeadingHintInputs(heading: string, hint: string): string {
  return `
    <div class="q-meta-row">
      <label class="q-meta-field">
        <span class="q-meta-label">Heading</span>
        <input
          class="q-meta-input"
          type="text"
          value="${escapeHtml(heading)}"
          data-action="heading"
          placeholder="e.g. The Characters"
        />
      </label>
      <label class="q-meta-field">
        <span class="q-meta-label">Hint</span>
        <input
          class="q-meta-input"
          type="text"
          value="${escapeHtml(hint)}"
          data-action="hint"
          placeholder="One-line label hint shown above the question"
        />
      </label>
    </div>
  `;
}

function renderChoiceBlockCompact(q: ChoiceQuestion, index: number): string {
  const selectedSet = new Set(q.selected);
  const hintHtml = q.hint ? `<div class="q-hint">${escapeHtml(q.hint)}</div>` : '';
  // TODO(targets-multi-select): reinstate "Select up to N" hint when multi-select ships.
  const chipsHtml = q.options
    .map((opt, oi) => {
      const isSelected = selectedSet.has(oi);
      return `
        <button
          type="button"
          class="option-chip${isSelected ? ' selected' : ''}"
          data-action="select"
          data-o="${oi}"
        >${escapeHtml(opt.label)}</button>
      `;
    })
    .join('');

  const locked = state.lockedQuestionIds.has(q.id);
  return `
    <div class="question-block compact${locked ? ' locked' : ''}" data-q-index="${index}" data-color="${q.colorIndex}" data-kind="choice">
      <div class="q-header-compact">
        <span class="q-number">Q${index}</span>
        <span class="q-title-static">${escapeHtml(q.title)}</span>
        ${renderRandomModeBtn(q.randomMode)}
        ${renderLockBtn(locked)}
      </div>
      ${hintHtml}
      <div class="option-chips">${chipsHtml}</div>
    </div>
  `;
}

function renderRandomModeBtn(mode: 'shuffle' | 'cycle'): string {
  const shuffleIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>`;
  const cycleIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>`;
  const isCycle = mode === 'cycle';
  const label = isCycle ? 'Cycle through options' : 'Shuffle options randomly';
  return `<button type="button" class="q-random-mode-btn${isCycle ? ' cycle' : ''}" data-action="toggle-random-mode" aria-label="${label}" title="${label}">${isCycle ? cycleIcon : shuffleIcon}</button>`;
}

function renderLockBtn(locked: boolean): string {
  const lockedIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
  const unlockedIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>`;
  return `<button type="button" class="q-lock-btn${locked ? ' locked' : ''}" data-action="toggle-lock" aria-label="${locked ? 'Unlock' : 'Lock'} selection" title="${locked ? 'Unlock' : 'Lock'} — exclude from randomize">${locked ? lockedIcon : unlockedIcon}</button>`;
}

interface BlockCtx {
  el: HTMLElement;
  index: number;
  kind: 'choice' | 'photo';
}

function blockFor(target: EventTarget | null): BlockCtx | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest<HTMLElement>('.question-block');
  if (!el) return null;
  return {
    el,
    index: Number(el.dataset.qIndex),
    kind: el.dataset.kind === 'photo' ? 'photo' : 'choice',
  };
}

function actionOf(target: EventTarget | null): {
  action: string;
  optionIndex: number;
  promptIndex: number;
  kind: string;
} | null {
  if (!(target instanceof HTMLElement)) return null;
  const el = target.closest<HTMLElement>('[data-action]');
  if (!el) return null;
  return {
    action: el.dataset.action ?? '',
    optionIndex: el.dataset.o !== undefined ? Number(el.dataset.o) : -1,
    promptIndex: el.dataset.p !== undefined ? Number(el.dataset.p) : -1,
    kind: el.dataset.kind ?? '',
  };
}

function promptLocator(
  target: EventTarget | null,
): { optionIndex: number; promptIndex: number } | null {
  if (!(target instanceof Element)) return null;
  const item = target.closest<HTMLElement>('.prompt-item');
  if (!item) return null;
  const o = Number(item.dataset.o);
  const p = Number(item.dataset.p);
  if (!Number.isFinite(o) || !Number.isFinite(p)) return null;
  return { optionIndex: o, promptIndex: p };
}

function onClick(e: Event): void {
  const block = blockFor(e.target);
  if (!block) return;
  const action = actionOf(e.target);
  if (!action) return;
  const q = state.questions[block.index];

  if (action.action === 'toggle-lock') {
    if (state.lockedQuestionIds.has(q.id)) state.lockedQuestionIds.delete(q.id);
    else state.lockedQuestionIds.add(q.id);
    notifyStructure();
    return;
  }

  if (action.action === 'toggle-random-mode') {
    q.randomMode = q.randomMode === 'cycle' ? 'shuffle' : 'cycle';
    q.cycleIndex = 0;
    notifyStructure();
    return;
  }

  if (action.action === 'toggle') {
    if (e.target instanceof HTMLElement && e.target.closest('[data-stop-toggle]')) return;
    block.el.classList.toggle('collapsed');
    return;
  }

  if (action.action === 'add-prompt') {
    if (!state.questionsEditMode) return;
    const prompts = promptsContainerFor(q, action.optionIndex);
    if (!prompts) return;
    const fresh: AnswerPrompt = {
      id: freshPromptId(),
      alternatives: [{ target: '', text: '', shortLabel: '' }],
    };
    prompts.push(fresh);
    notifyStructure();
    return;
  }

  if (action.action === 'delete-prompt') {
    if (!state.questionsEditMode) return;
    const loc = promptLocator(e.target);
    if (!loc) return;
    const prompts = promptsContainerFor(q, loc.optionIndex);
    if (!prompts) return;
    prompts.splice(loc.promptIndex, 1);
    notifyStructure();
    return;
  }

  if (action.action === 'add-alt') {
    if (!state.questionsEditMode) return;
    const loc = promptLocator(e.target);
    if (!loc) return;
    const prompts = promptsContainerFor(q, loc.optionIndex);
    const prompt = prompts?.[loc.promptIndex];
    if (!prompt) return;
    prompt.alternatives.push({ target: '', text: '', shortLabel: '' });
    notifyStructure();
    return;
  }

  if (action.action === 'delete-alt') {
    if (!state.questionsEditMode) return;
    const loc = promptLocator(e.target);
    if (!loc) return;
    const prompts = promptsContainerFor(q, loc.optionIndex);
    const prompt = prompts?.[loc.promptIndex];
    if (!prompt) return;
    const altIdx = Number((e.target as HTMLElement).dataset.alt);
    if (!Number.isFinite(altIdx)) return;
    prompt.alternatives.splice(altIdx, 1);
    if (prompt.alternatives.length === 0) {
      prompts!.splice(loc.promptIndex, 1);
    }
    notifyStructure();
    return;
  }

  if (block.kind === 'photo' && isPhoto(q)) {
    handlePhotoAction(q, action.action);
    return;
  }

  if (!isChoice(q)) return;

  switch (action.action) {
    case 'select': {
      const idx = action.optionIndex;
      // TODO(targets-multi-select): restore multi-select (maxSelections) once conflict resolution lands.
      if (q.selected.length === 1 && q.selected[0] === idx) {
        q.selected = [];
      } else {
        q.selected = [idx];
      }
      notifyStructure();
      break;
    }
    case 'add':
      if (!state.questionsEditMode) return;
      q.options.push({
        label: 'New option',
        injects: true,
        prompts: [],
      });
      notifyStructure();
      break;
    case 'remove':
      if (!state.questionsEditMode) return;
      if (q.options.length <= 2) return;
      q.selected = q.selected
        .filter((s) => s !== action.optionIndex)
        .map((s) => (s > action.optionIndex ? s - 1 : s));
      q.options.splice(action.optionIndex, 1);
      notifyStructure();
      break;
  }
}

function onInput(e: Event): void {
  if (!state.questionsEditMode) return;
  const block = blockFor(e.target);
  if (!block) return;
  const action = actionOf(e.target);
  if (!action) return;
  const q = state.questions[block.index];

  if (action.action === 'heading' && e.target instanceof HTMLInputElement) {
    q.heading = e.target.value;
    return;
  }
  if (action.action === 'hint' && e.target instanceof HTMLInputElement) {
    q.hint = e.target.value;
    return;
  }
  if (action.action === 'title' && e.target instanceof HTMLInputElement) {
    q.title = e.target.value;
    return;
  }

  if (isChoice(q) && action.action === 'label' && e.target instanceof HTMLInputElement) {
    q.options[action.optionIndex].label = e.target.value;
    notifyContent();
    return;
  }

  const loc = promptLocator(e.target);
  if (!loc) return;
  const prompts = promptsContainerFor(q, loc.optionIndex);
  const prompt = prompts?.[loc.promptIndex];
  if (!prompt) return;

  const altIdx = Number((e.target as HTMLElement).dataset.alt);
  if (!Number.isFinite(altIdx)) return;
  const alt = prompt.alternatives[altIdx];
  if (!alt) return;

  if (
    action.action === 'alt-target' &&
    (e.target instanceof HTMLSelectElement || e.target instanceof HTMLInputElement)
  ) {
    alt.target = e.target.value;
    notifyContent();
  } else if (action.action === 'alt-text' && e.target instanceof HTMLTextAreaElement) {
    alt.text = e.target.value;
    notifyContent();
  }
}

function onChange(e: Event): void {
  const block = blockFor(e.target);
  if (!block) return;
  const action = actionOf(e.target);
  if (!action) return;
  const q = state.questions[block.index];

  if (action.action === 'photo-file' && isPhoto(q) && e.target instanceof HTMLInputElement) {
    q.file = e.target.files?.[0] ?? null;
    notifyStructure();
  }
}

function handlePhotoAction(q: PhotoQuestion, action: string): void {
  if (action === 'photo-webcam') {
    void openWebcamModal((file) => {
      q.file = file;
      notifyStructure();
    });
    return;
  }
  if (action === 'photo-portrait') {
    void openPortraitPicker((file) => {
      q.file = file;
      notifyStructure();
    });
    return;
  }
  if (action === 'photo-clear') {
    q.file = null;
    notifyStructure();
  }
}

function dropzoneFor(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest<HTMLElement>('.photo-dropzone');
}

function onDragEnter(e: DragEvent): void {
  const dropzone = dropzoneFor(e.target);
  if (!dropzone) return;
  e.preventDefault();
  dropzone.classList.add('dragging');
}

function onDragOver(e: DragEvent): void {
  const dropzone = dropzoneFor(e.target);
  if (dropzone) {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    dropzone.classList.add('dragging');
    return;
  }
  // Prompt reorder within an answer
  if (!hasPromptDrag(e)) return;
  if (!(e.target instanceof HTMLElement)) return;
  const item = e.target.closest<HTMLElement>('.prompt-item');
  if (!item) return;
  e.preventDefault();
  e.stopPropagation();
  const rect = item.getBoundingClientRect();
  const above = e.clientY - rect.top < rect.height / 2;
  item.classList.toggle('drop-above', above);
  item.classList.toggle('drop-below', !above);
}

function onDragLeave(e: DragEvent): void {
  const dropzone = dropzoneFor(e.target);
  if (dropzone) {
    const related = e.relatedTarget;
    if (related instanceof Node && dropzone.contains(related)) return;
    dropzone.classList.remove('dragging');
    return;
  }
  if (!(e.target instanceof HTMLElement)) return;
  const item = e.target.closest<HTMLElement>('.prompt-item');
  if (item) item.classList.remove('drop-above', 'drop-below');
}

function onDrop(e: DragEvent): void {
  const dropzone = dropzoneFor(e.target);
  if (dropzone) {
    e.preventDefault();
    dropzone.classList.remove('dragging');

    const block = blockFor(e.target);
    if (!block || block.kind !== 'photo') return;
    const q = state.questions[block.index];
    if (!isPhoto(q)) return;

    const file = e.dataTransfer?.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    q.file = file;
    notifyStructure();
    return;
  }

  // Prompt reorder drop
  if (!(e.target instanceof HTMLElement)) return;
  const item = e.target.closest<HTMLElement>('.prompt-item');
  if (!item) return;
  const data = e.dataTransfer?.getData('text/plain') ?? '';
  if (!data.startsWith('prompt:')) return;
  e.preventDefault();
  e.stopPropagation();
  const fromId = data.slice('prompt:'.length);
  const above = item.classList.contains('drop-above');
  item.classList.remove('drop-above', 'drop-below');
  reorderPrompt(item, fromId, above);
}

function onDragStart(e: DragEvent): void {
  if (!(e.target instanceof HTMLElement)) return;
  const item = e.target.closest<HTMLElement>('.prompt-item');
  if (!item) return;
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
    e.preventDefault();
    return;
  }
  const promptId = item.dataset.promptId;
  if (!promptId) return;
  e.dataTransfer?.setData('text/plain', `prompt:${promptId}`);
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  item.classList.add('is-dragging');
}

function onDragEnd(e: DragEvent): void {
  if (!(e.target instanceof HTMLElement)) return;
  const item = e.target.closest<HTMLElement>('.prompt-item');
  if (item) item.classList.remove('is-dragging', 'drop-above', 'drop-below');
}

function hasPromptDrag(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('text/plain');
}

function reorderPrompt(toItem: HTMLElement, fromId: string, above: boolean): void {
  const block = blockFor(toItem);
  if (!block) return;
  const q = state.questions[block.index];
  const toOptIdx = Number(toItem.dataset.o);
  const toPromptIdx = Number(toItem.dataset.p);
  const prompts = promptsContainerFor(q, toOptIdx);
  if (!prompts) return;
  const fromIdx = prompts.findIndex((p) => p.id === fromId);
  // Cross-answer reorder is intentionally unsupported for now.
  if (fromIdx < 0 || fromIdx === toPromptIdx) return;
  const [moving] = prompts.splice(fromIdx, 1);
  let insertAt = toPromptIdx;
  if (fromIdx < toPromptIdx) insertAt -= 1;
  if (!above) insertAt += 1;
  insertAt = Math.max(0, Math.min(insertAt, prompts.length));
  prompts.splice(insertAt, 0, moving);
  notifyStructure();
}
